import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Overlay from "../../../checkEligibilty/components/modals/Overlay";
import { motion } from "framer-motion";
import { ContainerVariants } from "../../../checkEligibilty/components/modals/variant";
import { IoMdClose } from "react-icons/io";
import { PlayCircle, Sparkles } from "lucide-react";

interface Props {
  onClose: () => void;
  onOpenMainModal: () => void;
}

const PreInterviewModal = ({ onClose, onOpenMainModal }: Props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [microcopyIndex, setMicrocopyIndex] = useState(0);

  const microcopyVariations = [
    {
      text: "Ready to ace your visa interview? Let's start now!",
      emphasis: "ace your visa interview",
      rationale: "Urgency-focused with confidence building",
    },
    {
      text: "Test your interview skills in under 60 seconds",
      emphasis: "under 60 seconds",
      rationale: "Time-bound challenge creates urgency",
    },
    {
      text: "Think you can handle the tough questions?",
      emphasis: "tough questions",
      rationale: "Challenge-oriented to trigger competitive spirit",
    },
  ];

  useEffect(() => {
    // Trigger entrance animation immediately
    const timer = setTimeout(() => setIsLoaded(true), 100);

    // Cycle through microcopy variations for demonstration
    const microcopyTimer = setInterval(() => {
      setMicrocopyIndex((prev) => (prev + 1) % microcopyVariations.length);
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearInterval(microcopyTimer);
    };
  }, [microcopyVariations.length]);

  const handleTapToBegin = () => {
    onOpenMainModal();
  };

  return createPortal(
    <Overlay onClose={onClose}>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        variants={ContainerVariants}
        className=" w-[90vw] sm:w-[600px] h-max bg-gray-100 rounded-xl sm:px-0"
      >
        <div className="h-full py-10 px-4 relative pb-0">
          <div className="w-full mx-auto">
            <div className="mb-8">
              <IoMdClose
                onClick={onClose}
                className="cursor-pointer absolute right-3 top-2 bg-white"
                size={24}
              />
            </div>

            <div className="flex p-4 flex-col items-center pb-10">
              {/* Main content container */}

              {/* Header with icon */}
              <div className="mb-10">
                <div className="w-16 h-16 bg-primaryColor rounded-full flex items-center justify-center mx-auto animate-bounce">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>

                {/* Main headline */}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 leading-tight text-center">
                  Think You're Interview-Ready?{" "}
                  <span className="bg-primaryColor bg-clip-text text-transparent">
                    Try This Visa Question!
                  </span>
                </h1>

                {/* Dynamic microcopy */}
                <p
                  className={`
            text-lg text-gray-600 transition-opacity duration-500 text-center
            ${isLoaded ? "opacity-100" : "opacity-0"}
          `}
                >
                  {microcopyVariations[microcopyIndex].text
                    .split(microcopyVariations[microcopyIndex].emphasis)
                    .map((part, index, array) =>
                      index === 0 ? (
                        <span key={index}>{part}</span>
                      ) : (
                        <React.Fragment key={index}>
                          <span className="font-semibold text-primaryColor">
                            {microcopyVariations[microcopyIndex].emphasis}
                          </span>
                          {array[index]}
                        </React.Fragment>
                      )
                    )}
                </p>
              </div>

              {/* Pulsing CTA button */}
              <button
                onClick={handleTapToBegin}
                className={`
            group relative bg-primaryColor text-white
            px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg
            hover:shadow-xl transform hover:scale-105 transition-all duration-300
            animate-pulse hover:animate-none
            ${isLoaded ? "opacity-100" : "opacity-0"}
          `}
                style={{
                  animationDuration: "2s",
                }}
              >
                <span className="flex items-center justify-center">
                  <PlayCircle className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-300" />
                  Tap to Begin Interview
                </span>

                {/* Shimmer effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transform -skew-x-12 group-hover:animate-shimmer"></div>
              </button>

              <p className="text-xs text-gray-600 mt-6 text-center">
                💡 Don't join the 75% of U.S. visa applicants who get denied—get
                this question right
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </Overlay>,
    document.body
  );
};

export default PreInterviewModal;
